import * as React from "react";
import { createContext, useContext } from "react";
import styled from "styled-components";
import cn from "classnames";

import { safeID } from "../utils/safeID";
import { themeValues } from "../theme";

interface IFormControlContext {
  id: string;
  disabled?: boolean
}

export const FormControlContext = createContext<IFormControlContext | null>({
  id: "",
  disabled: false,
});

interface IStyledFormControlProps {
  full?: boolean;
}

interface IFormControlProps extends IStyledFormControlProps {
  id?: string;
  children: React.ReactNode;
  disabled?: boolean;
}

export function FormControl({ id: idProp, full, children, disabled }: IFormControlProps): JSX.Element {
  const id = idProp || safeID("form_control")("");

  return <FormControlContext.Provider value={{ id, disabled }}>
    <StyledFormControl className={cn({
      "fullwidth": full,
      "disabled": disabled,
    })}>
      {children}
    </StyledFormControl>
  </FormControlContext.Provider>;
}

export const StyledFormControlLabel = styled.label`
  cursor: pointer;
  display: flex;
  align-self: flex-start;
  color: #000;
  margin-bottom: 8px;

  &.spacing-default {
    gap: 5px;
  }

  &.spacing-small {
    gap: 2px;
  }

  &.spacing-medium {
    gap: 10px;
  }

  &.spacing-large {
    gap: 15px;
  }

  &.append {
    width: 100%;
    align-items: center;
  }
`;
StyledFormControlLabel.displayName = "StyledFormControlLabel";

export const StyledFormControl = styled.div<IStyledFormControlProps>`
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
  max-width: 400px;

  &.fullwidth {
    max-width: 100%;
  }

  &.disabled {
    ${StyledFormControlLabel} {
      color: ${themeValues.disabledColor}
    }
    
    pointer-events: none;
  }
`;
StyledFormControl.displayName = "StyledFormControl";

const StyledAppendLabelWrapper = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${StyledFormControlLabel} {
    margin-bottom: 0;
  }
`;
StyledAppendLabelWrapper.displayName = "StyledAppendLabelWrapper";

interface IFormControlLabelProps {
  spacing?: boolean | "default" | "small" | "medium" | "large";
  append?: React.ReactNode;
  for?: string;
  text?: string;
  children?: React.ReactNode;
}

export function FormControlLabel({
                                   for: forProp,
                                   text,
                                   children,
                                   spacing: spacingProp,
                                   append
                                 }: IFormControlLabelProps): JSX.Element {
  const formControl = useContext(FormControlContext);
  const htmlFor = forProp || formControl?.id;

  let spacing = "";

  if (spacingProp) {
    spacing = typeof spacingProp === "boolean" ? "default" : spacingProp;
  }

  const label = <StyledFormControlLabel
    htmlFor={htmlFor}
    className={cn({
      [`spacing-${spacing}`]: spacing,
      "append": append
    })}
  >
    {text || children}
  </StyledFormControlLabel>;

  if (!append) {
    return label;
  }

  return <StyledAppendLabelWrapper>
    <div>
      {label}
    </div>
    <div>
      {append}
    </div>
  </StyledAppendLabelWrapper>;
}

export const StyledFormControlGroup = styled.div`
  &.inline {
    display: flex;

    ${StyledFormControl} {
      margin-bottom: 0;
    }

    &.inline-default {
      gap: 20px;
    }

    &.inline-small {
      gap: 16px;
    }

    &.inline-medium {
      gap: 25px;
    }

    &.inline-large {
      gap: 35px;
    }
  }

  ${StyledFormControl} {
    flex-direction: initial;
    align-items: center;
  }

  ${StyledFormControlLabel} {
    align-items: center;
  }
`;
StyledFormControlGroup.displayName = "StyledFormControlGroup";

interface IFormControlGroupProps {
  inline?: boolean | "default" | "small" | "medium" | "large";
  children: React.ReactNode;
}

export function FormControlGroup({
                                   inline: inlineProp,
                                   children
                                 }: IFormControlGroupProps): JSX.Element {

  let inline = "";

  if (inlineProp) {
    inline = typeof inlineProp === "boolean" ? "default" : inlineProp;
  }

  return <StyledFormControlGroup
    className={cn({
      "inline": inline,
      [`inline-${inline}`]: inline
    })}
  >
    {children}
  </StyledFormControlGroup>;
}